import { Reducer } from 'redux';
import { createAction, ActionTypes } from 'client/core/store/actions';
import { useSelector, shallowEqual } from 'react-redux';
import { RootState } from 'client/core/store/RootReducer';
import { IClientConfig } from 'common/config/IClientConfig';
import { fromPairs } from 'lodash';
import { IClientModuleConfig } from 'common/config/IModulesConfig';

export const ClientConfigActions = {
  loaded: createAction('@clientConfig/loaded', (config: IClientConfig) => ({
    config
  }))
};

export interface ClientConfigState {
  config: IClientConfig | undefined;
  transformedModules: {
    [key: string]: IClientModuleConfig;
  };
  transformedTheme: {
    options: { [key: string]: any };
    labels: { [key: string]: string };
  };
}

export const ClientConfigReducer: Reducer<
  ClientConfigState,
  ActionTypes<typeof ClientConfigActions>
> = (
  state = {
    config: undefined,
    transformedModules: {},
    transformedTheme: {
      options: {},
      labels: {}
    }
  },
  action
) => {
  switch (action.type) {
    /**
     * Al caricamento, salviamo la configurazione
     */
    case '@clientConfig/loaded':
      return {
        ...state,
        config: action.payload.config,
        transformedModules: fromPairs(
          action.payload.config.modules.map(m => [m.feature, m])
        ),
        transformedTheme: {
          options: fromPairs(
            action.payload.config.theme.options.map(o => [o.name, o.value])
          ),
          labels: fromPairs(
            action.payload.config.theme.labels.map(l => [l.name, l.text])
          )
        }
      };

    default:
      return state;
  }
};

/**
 * Hook per ottenere la configurazione corrente
 */
export const useClientConfig = () =>
  useSelector((state: RootState) => state.config.config, shallowEqual);

/**
 * Hook per ottenere le informazioni su una strategia di autenticazione
 */
export const useSecurityStrategyConfig = (type: string) =>
  useSelector(
    (state: RootState) =>
      state.config.config?.security.strategies.find(s => s.type === type),
    shallowEqual
  );

/**
 * Hook per ottenere le opzioni di configurazione per l'UI (tema)
 */
export const useThemeConfig = () =>
  useSelector(
    (state: RootState) => state.config.transformedTheme,
    shallowEqual
  );

/**
 * Hook per ottenere una label
 */
export const useThemeLabel = (name: string) =>
  useSelector(
    (state: RootState) => state.config.transformedTheme.labels[name],
    shallowEqual
  );

/**
 * Hook per ottenere le opzioni di configurazione per dei moduli
 */
export const useModulesConfig = () =>
  useSelector(
    (state: RootState) => state.config.transformedModules,
    shallowEqual
  );

export const useModuleConfigOption = (feature: string, option: string) =>
  useSelector(
    (state: RootState) =>
      state.config.transformedModules[feature]?.options?.find(
        o => o.name === option
      ),
    shallowEqual
  );

export const useModuleConfigOptions = (feature: string) =>
  useSelector(
    (state: RootState) => state.config.transformedModules[feature]?.options,
    shallowEqual
  );

export const useHasFeature = (feature: string) =>
  useSelector(
    (state: RootState) => !!state.config.transformedModules[feature],
    shallowEqual
  );

export const useModuleConfigEnum = (feature: string, key: string) =>
  useSelector(
    (state: RootState) =>
      state.config.transformedModules[feature]?.enums?.find(o => o.key === key),
    shallowEqual
  );
